import { doc, collection, query, where, orderBy, limit, getDoc, getDocs, onSnapshot, writeBatch, deleteField } from 'firebase/firestore';
import { isFullString, isNumber, isArray } from 'is-what';
import { isDocModule, isCollectionModule } from '@magnetarjs/core';
import { merge } from 'merge-anything';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function logError(errorMessage) {
    console.error('[@magnetarjs/plugin-firestore error]\n', errorMessage);
}
function logErrorAndThrow(errorMessage) {
    logError(errorMessage);
    throw new Error(errorMessage);
}
function throwIfInvalidFirestorePath(firestorePath, moduleType) {
    let errorMessage = '';
    // no firestorePath found
    if (!isFullString(firestorePath)) {
        errorMessage = `Firestore "path" not found.
    You can enable \`useModulePathsForFirestore\` in the Firestore plugin options to automatically use the same paths in Firestore as your modules.
    Otherwise you have to set the firestorePath on your doc/collection like so: \`doc('myCollection/myDoc', { configPerStore: { [storePluginName]: { firestorePath: 'myFirestorePath/someDoc' } } })\``;
    }
    // sanity check for collections
    else if (moduleType === 'collection') {
        if (isDocModule(firestorePath))
            errorMessage = `Your collection id (or "path") must be of odd segments. The expected pattern is: collection/doc/collection ... Yours was ${firestorePath}`;
    }
    // sanity check for docs
    else if (moduleType === 'doc') {
        if (isCollectionModule(firestorePath))
            errorMessage = `Your doc id (or "path") must be of even segments. The expected pattern is: collection/doc/collection/doc ... Yours was ${firestorePath}`;
    }
    if (errorMessage)
        logErrorAndThrow(errorMessage);
}

function getFirestoreDocPath(collectionPath, docId, firestoreModuleConfig, firestorePluginOptions) {
    let documentPath;
    // if firestorePath is set on the module level, always return this
    const { firestorePath } = firestoreModuleConfig;
    if (isFullString(firestorePath)) {
        documentPath = isCollectionModule(firestorePath)
            ? [firestorePath, docId].join('/')
            : firestorePath;
    }
    else {
        // else, return the modulePath only if this option is enabled in the global firestorePluginOptions
        const { useModulePathsForFirestore } = firestorePluginOptions;
        const modulePath = [collectionPath, docId].join('/');
        documentPath = (useModulePathsForFirestore ? modulePath : firestorePath);
    }
    throwIfInvalidFirestorePath(documentPath, 'doc');
    return documentPath;
}
function getFirestoreCollectionPath(_collectionPath, firestoreModuleConfig, firestorePluginOptions) {
    let collectionPath;
    // if firestorePath is set on the module level, always return this
    const { firestorePath } = firestoreModuleConfig;
    if (isFullString(firestorePath)) {
        collectionPath = firestorePath;
    }
    else {
        // else, return the modulePath only if this option is enabled in the global firestorePluginOptions
        const { useModulePathsForFirestore } = firestorePluginOptions;
        collectionPath = (useModulePathsForFirestore ? _collectionPath : firestorePath);
    }
    throwIfInvalidFirestorePath(collectionPath, 'collection');
    return collectionPath;
}

function insertActionFactory(batchSync, firestorePluginOptions) {
    return function ({ payload, collectionPath, docId, actionConfig, pluginModuleConfig, }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { db } = firestorePluginOptions;
            let _docId = docId;
            if (!_docId) {
                // we don't have a _docId, so we need to retrieve it from the payload or generate one
                _docId =
                    isFullString(payload.id) || isNumber(payload.id)
                        ? String(payload.id)
                        : doc(collection(db, 'random')).id;
            }
            const documentPath = getFirestoreDocPath(collectionPath, _docId, pluginModuleConfig, firestorePluginOptions); // prettier-ignore
            const syncDebounceMs = isNumber(actionConfig.syncDebounceMs)
                ? actionConfig.syncDebounceMs
                : pluginModuleConfig.syncDebounceMs;
            const result = yield batchSync.insert(documentPath, payload, syncDebounceMs);
            return [_docId, result];
        });
    };
}

function writeActionFactory(batchSync, firestorePluginOptions, actionName) {
    return function ({ payload, collectionPath, docId, actionConfig, pluginModuleConfig, }) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!docId)
                throw new Error('An non-existent action was triggered on a collection');
            const documentPath = getFirestoreDocPath(collectionPath, docId, pluginModuleConfig, firestorePluginOptions); // prettier-ignore
            const syncDebounceMs = isNumber(actionConfig.syncDebounceMs)
                ? actionConfig.syncDebounceMs
                : pluginModuleConfig.syncDebounceMs;
            if (actionName === 'assign') {
                const result = yield batchSync.assign(documentPath, payload, syncDebounceMs);
                return result;
            }
            if (actionName === 'replace') {
                const result = yield batchSync.replace(documentPath, payload, syncDebounceMs);
                return result;
            }
            // (actionName === 'merge')
            const result = yield batchSync.merge(documentPath, payload, syncDebounceMs);
            return result;
        });
    };
}

function deletePropActionFactory(batchSync, firestorePluginOptions) {
    return function ({ payload, collectionPath, docId, actionConfig, pluginModuleConfig, }) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!docId)
                throw new Error('An non-existent action was triggered on a collection');
            const documentPath = getFirestoreDocPath(collectionPath, docId, pluginModuleConfig, firestorePluginOptions); // prettier-ignore
            const payloadArray = isArray(payload) ? payload : [payload];
            const syncDebounceMs = isNumber(actionConfig.syncDebounceMs)
                ? actionConfig.syncDebounceMs
                : pluginModuleConfig.syncDebounceMs;
            const result = yield batchSync.deleteProp(documentPath, payloadArray, syncDebounceMs);
            return result;
        });
    };
}

function deleteActionFactory(batchSync, firestorePluginOptions) {
    return function ({ payload, collectionPath, docId, actionConfig, pluginModuleConfig, }) {
        return __awaiter(this, void 0, void 0, function* () {
            const _docId = docId || payload;
            if (!isFullString(_docId))
                throw new Error('No ID passed to delete action.');
            const documentPath = getFirestoreDocPath(collectionPath, _docId, pluginModuleConfig, firestorePluginOptions); // prettier-ignore
            const syncDebounceMs = isNumber(actionConfig.syncDebounceMs)
                ? actionConfig.syncDebounceMs
                : pluginModuleConfig.syncDebounceMs;
            const result = yield batchSync.delete(documentPath, syncDebounceMs);
            return result;
        });
    };
}

function getQueryInstance(collectionPath, config, db) {
    let q;
    q = collection(db, collectionPath);
    for (const whereClause of config.where || []) {
        q = query(q, where(...whereClause));
    }
    for (const orderByClause of config.orderBy || []) {
        q = query(q, orderBy(...orderByClause));
    }
    if (isNumber(config.limit)) {
        q = query(q, limit(config.limit));
    }
    return q;
}
function docSnapshotToDocMetadata(docSnapshot) {
    const docMetaData = {
        data: docSnapshot.data(),
        metadata: docSnapshot,
        id: docSnapshot.id,
        exists: docSnapshot.exists(),
    };
    return docMetaData;
}

function fetchActionFactory(firestorePluginOptions) {
    return function ({ payload, collectionPath, docId, pluginModuleConfig, }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { db } = firestorePluginOptions;
            // in case of a doc module
            let snapshots;
            if (docId) {
                const documentPath = getFirestoreDocPath(collectionPath, docId, pluginModuleConfig, firestorePluginOptions); // prettier-ignore
                const docSnapshot = yield getDoc(doc(db, documentPath));
                snapshots = [docSnapshot];
            }
            // in case of a collection module
            else if (!docId) {
                const _collectionPath = getFirestoreCollectionPath(collectionPath, pluginModuleConfig, firestorePluginOptions); // prettier-ignore
                const queryInstance = getQueryInstance(_collectionPath, pluginModuleConfig, db);
                const querySnapshot = yield getDocs(queryInstance);
                snapshots = querySnapshot.docs;
            }
            if (!snapshots)
                return { docs: [] };
            // map snapshots to DocMetadata type
            const docs = snapshots.map(docSnapshotToDocMetadata);
            return { docs };
        });
    };
}

function streamActionFactory(firestorePluginOptions) {
    return function ({ payload, collectionPath, docId, pluginModuleConfig, mustExecuteOnRead, }) {
        const { added, modified, removed } = mustExecuteOnRead;
        const { db } = firestorePluginOptions;
        let resolveStream;
        let rejectStream;
        const streaming = new Promise((resolve, reject) => {
            resolveStream = resolve;
            rejectStream = reject;
        });
        let closeStreamStream;
        // in case of a doc module
        if (docId) {
            const documentPath = getFirestoreDocPath(collectionPath, docId, pluginModuleConfig, firestorePluginOptions); // prettier-ignore
            closeStreamStream = onSnapshot(doc(db, documentPath), (docSnapshot) => {
                const localChange = docSnapshot.metadata.hasPendingWrites;
                // do nothing for local changes
                if (localChange)
                    return;
                // do nothing if the doc doesn't exist
                if (!docSnapshot.exists)
                    return;
                // serverChanges only
                const docData = docSnapshot.data();
                const docMetadata = docSnapshotToDocMetadata(docSnapshot);
                added(docData, docMetadata);
            }, rejectStream);
        }
        // in case of a collection module
        else if (!docId) {
            const _collectionPath = getFirestoreCollectionPath(collectionPath, pluginModuleConfig, firestorePluginOptions); // prettier-ignore
            const queryInstance = getQueryInstance(_collectionPath, pluginModuleConfig, db);
            closeStreamStream = onSnapshot(queryInstance, (querySnapshot) => {
                // do nothing for local changes
                const localChanges = querySnapshot.metadata.hasPendingWrites;
                if (localChanges)
                    return;
                // serverChanges only
                querySnapshot.docChanges().forEach((docChange) => {
                    const docSnapshot = docChange.doc;
                    const docData = docSnapshot.data();
                    const docMetadata = docSnapshotToDocMetadata(docSnapshot);
                    if (docChange.type === 'added') {
                        added(docData, docMetadata);
                    }
                    if (docChange.type === 'modified') {
                        modified(docData, docMetadata);
                    }
                    if (docChange.type === 'removed') {
                        removed(docData, docMetadata);
                    }
                });
            }, rejectStream);
        }
        function stop() {
            if (resolveStream)
                resolveStream();
            closeStreamStream();
        }
        return { stop, streaming };
    };
}

function revertActionFactory(actions, firestorePluginOptions) {
    return function ({ payload, collectionPath, docId, actionConfig, pluginModuleConfig, actionName, error, }) {
        return __awaiter(this, void 0, void 0, function* () {
            // reverting on read actions is not neccesary
            const isReadAction = ['fetch', 'stream'].includes(actionName);
            if (isReadAction)
                return;
            // revert all write actions when called on a doc
            if (docId) {
                if (actionName === 'insert') {
                    if (actions.delete)
                        yield actions.delete({
                            payload: undefined,
                            collectionPath,
                            docId,
                            actionConfig,
                            pluginModuleConfig,
                        });
                    return;
                }
            }
            // other actions don't need revert
        });
    };
}

/**
 * A countdown which can be restarted and resolves when the provided milliseconds have passed.
 *
 * @param {number} ms The amount of milliseconds to count down.
 * @returns {{done: Promise<void>, restart: () => void}} restart will reset the countdown and start counting down again.
 * @example
 * const countdown = Countdown(1000)
 * // set up what to do when it's finished:
 * countdown.done.then(() => doSomething())
 * // call this every time to restart the countdown:
 * countdown.restart()
 * @author Adam Dorling
 * @contact https://codepen.io/naito
 */
function Countdown(ms) {
    let startTime = Date.now();
    let interval = null;
    let resolveTrigger = null;
    const done = new Promise((resolve) => (resolveTrigger = resolve));
    function finish() {
        clearInterval(interval);
        interval = null;
        if (resolveTrigger) {
            resolveTrigger();
            resolveTrigger = null;
        }
    }
    function restart() {
        startTime = Date.now();
    }
    interval = setInterval(() => {
        const now = Date.now();
        const deltaT = now - startTime;
        if (deltaT >= ms)
            finish();
    }, 10);
    return { done, restart, forceFinish: finish };
}

// https://firebase.google.com/docs/firestore/manage-data/transactions#batched-writes
// A batched write can contain up to 500 operations.
const MAX_OPERATION_COUNT = 500;
const newStack = () => ({
    operationCount: 0,
    resolves: [],
    rejects: [],
    batch: { insert: new Map(), assign: new Map(), merge: new Map(), replace: new Map(), deleteProp: new Map(), delete: new Set() }, // prettier-ignore
});
/**
 * Each write operation in a batch counts towards the 500 limit.
 * Within a write operation, field transforms like serverTimestamp,
 * arrayUnion, and increment each count as an additional operation.
 *
 * @param {Record<string, any>} payload
 * @returns {number}
 */
function countOperations(payload) {
    const count = 1;
    // todo: when actions like serverTimestamp, arrayUnion and increment are supported, count them here
    return count;
}
function preparePayload(_payload) {
    // todo: properly handle any serverTimestamp, arrayUnion and increment in here
    const payload = _payload;
    const operationCount = countOperations();
    return { payload, operationCount };
}
function prepareReturnPromise(stack) {
    return new Promise((resolve, reject) => {
        stack.resolves.push(() => resolve(stack.batch));
        stack.rejects.push(reject);
    });
}
/**
 * Creates a BatchSync instance that will sync to firestore and automatically debounce
 *
 * @export
 * @returns {BatchSync}
 */
function batchSyncFactory(firestorePluginOptions) {
    const { db, syncDebounceMs, debug } = firestorePluginOptions;
    /**
     * A function that applies everything in the `SyncBatch` to a Firestore's `WriteBatch`.
     * It mutates the passed `batch`.
     */
    function applySyncBatch(writeBatch, batch) {
        batch.insert.forEach((payload, documentPath) => {
            const ref = doc(db, documentPath);
            writeBatch.set(ref, payload);
        });
        batch.assign.forEach((payload, documentPath) => {
            const ref = doc(db, documentPath);
            writeBatch.set(ref, payload, { mergeFields: Object.keys(payload) });
        });
        batch.merge.forEach((payload, documentPath) => {
            const ref = doc(db, documentPath);
            writeBatch.set(ref, payload, { merge: true });
        });
        batch.replace.forEach((payload, documentPath) => {
            const ref = doc(db, documentPath);
            writeBatch.set(ref, payload);
        });
        batch.deleteProp.forEach((payload, documentPath) => {
            const ref = doc(db, documentPath);
            const _payload = [...payload].reduce((carry, propPath) => (Object.assign(Object.assign({}, carry), { [propPath]: deleteField() })), {});
            writeBatch.update(ref, _payload);
        });
        batch.delete.forEach((documentPath) => {
            const ref = doc(db, documentPath);
            writeBatch.delete(ref);
        });
    }
    const state = {
        queue: [],
        countdown: null,
    };
    function prepareStack(operationCount, queueIndex = 0) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!state.queue[queueIndex])
                state.queue[queueIndex] = newStack();
            const stack = state.queue[queueIndex];
            if (stack.operationCount + operationCount >= MAX_OPERATION_COUNT) {
                return prepareStack(operationCount, queueIndex + 1);
            }
            stack.operationCount += operationCount;
            return stack;
        });
    }
    /**
     * Removes one `stack` entry from the `queue` & executes batch.commit() and makes sure to reject or resolve all actions when this promise is resolved
     */
    function executeSync() {
        state.countdown = null;
        const stack = state.queue.shift();
        if (!stack) {
            throw new Error('executeSync executed before it was instantiated');
        }
        const writeBatch$1 = writeBatch(db);
        try {
            applySyncBatch(writeBatch$1, stack.batch);
        }
        catch (error) {
            stack.rejects.forEach((rej) => rej(error));
            if (state.queue.length) {
                triggerSync(0);
            }
            return;
        }
        if (debug) {
            console.log('[magnetar] Syncing to firestore...', stack.batch);
        }
        writeBatch$1
            .commit()
            .then(() => stack.resolves.forEach((res) => res()))
            .catch((error) => stack.rejects.forEach((rej) => rej(error)))
            .finally(() => {
            if (state.queue.length) {
                triggerSync(0);
            }
        });
    }
    function forceSyncEarly() {
        return new Promise((resolve) => {
            if (!state.countdown)
                return resolve();
            state.countdown.done.then(() => resolve());
            state.countdown.forceFinish();
        });
    }
    /**
     * Sets a new countdown if it doesn't exist yet, and makes sure that the countdown will executeSync
     *
     * @param {number} [debounceMsOverwrite] Pass a number to set the batch sync countdown. If not set, it will use the globally set `syncDebounceMs`.
     * @returns {CountdownInstance}
     */
    function prepareCountdown(debounceMsOverwrite) {
        if (!state.countdown) {
            const ms = isNumber(debounceMsOverwrite) ? debounceMsOverwrite : syncDebounceMs;
            state.countdown = Countdown(ms);
            state.countdown.done.then(() => executeSync());
        }
        return state.countdown;
    }
    function triggerSync(debounceMsOverwrite) {
        const countdown = prepareCountdown(debounceMsOverwrite);
        countdown.restart();
    }
    function insert(documentPath, _payload, debounceMsOverwrite) {
        return __awaiter(this, void 0, void 0, function* () {
            const { payload, operationCount } = preparePayload(_payload);
            const stack = yield prepareStack(operationCount);
            stack.batch.insert.set(documentPath, payload);
            const promise = prepareReturnPromise(stack);
            triggerSync(debounceMsOverwrite);
            return promise;
        });
    }
    function assign(documentPath, _payload, debounceMsOverwrite) {
        return __awaiter(this, void 0, void 0, function* () {
            const { payload, operationCount } = preparePayload(_payload);
            let stack = yield prepareStack(operationCount);
            // flush! Because these writes cannot be combined
            if (stack.batch.merge.has(documentPath) || stack.batch.deleteProp.has(documentPath)) {
                yield forceSyncEarly();
                stack = yield prepareStack(operationCount);
            }
            // when queueing a `replace` we need to update this payload instead
            const replacePayload = stack.batch.replace.get(documentPath);
            if (replacePayload) {
                stack.batch.replace.set(documentPath, Object.assign(Object.assign({}, replacePayload), payload));
            }
            // otherwise combine with any previous payload
            if (!replacePayload) {
                const previousPayload = stack.batch.assign.get(documentPath);
                stack.batch.assign.set(documentPath, Object.assign(Object.assign({}, previousPayload), payload));
            }
            const promise = prepareReturnPromise(stack);
            triggerSync(debounceMsOverwrite);
            return promise;
        });
    }
    function merge$1(documentPath, _payload, debounceMsOverwrite) {
        return __awaiter(this, void 0, void 0, function* () {
            const { payload, operationCount } = preparePayload(_payload);
            let stack = yield prepareStack(operationCount);
            // flush! Because these writes cannot be combined
            if (stack.batch.assign.has(documentPath) || stack.batch.deleteProp.has(documentPath)) {
                yield forceSyncEarly();
                stack = yield prepareStack(operationCount);
            }
            // when queueing a `replace` we need to update this payload instead
            const replacePayload = stack.batch.replace.get(documentPath);
            if (replacePayload) {
                stack.batch.replace.set(documentPath, merge(replacePayload, payload));
            }
            // otherwise combine with any previous payload
            if (!replacePayload) {
                const previousPayload = stack.batch.merge.get(documentPath) || {};
                stack.batch.merge.set(documentPath, merge(previousPayload, payload));
            }
            const promise = prepareReturnPromise(stack);
            triggerSync(debounceMsOverwrite);
            return promise;
        });
    }
    function replace(documentPath, _payload, debounceMsOverwrite) {
        return __awaiter(this, void 0, void 0, function* () {
            const { payload, operationCount } = preparePayload(_payload);
            let stack = yield prepareStack(operationCount);
            // flush! Because these writes cannot be combined
            if (stack.batch.deleteProp.has(documentPath)) {
                yield forceSyncEarly();
                stack = yield prepareStack(operationCount);
            }
            stack.batch.replace.set(documentPath, payload);
            const promise = prepareReturnPromise(stack);
            triggerSync(debounceMsOverwrite);
            return promise;
        });
    }
    function deleteProp(documentPath, propPaths, debounceMsOverwrite) {
        return __awaiter(this, void 0, void 0, function* () {
            let operationCount = 0;
            const stack = yield prepareStack(operationCount);
            const map = stack.batch.deleteProp;
            if (!map.has(documentPath)) {
                map.set(documentPath, new Set());
                operationCount = 1;
            }
            const set = map.get(documentPath);
            propPaths.forEach((p) => set.add(p));
            const promise = prepareReturnPromise(stack);
            triggerSync(debounceMsOverwrite);
            return promise;
        });
    }
    function _delete(documentPath, debounceMsOverwrite) {
        return __awaiter(this, void 0, void 0, function* () {
            const operationCount = 0;
            const stack = yield prepareStack(operationCount);
            const promise = prepareReturnPromise(stack);
            stack.batch.delete.add(documentPath);
            triggerSync(debounceMsOverwrite);
            return promise;
        });
    }
    return { assign, merge: merge$1, replace, insert, deleteProp, delete: _delete, forceSyncEarly };
}

function firestorePluginOptionsWithDefaults(firestorePluginOptions) {
    return Object.assign({ syncDebounceMs: 1000, useModulePathsForFirestore: true, debug: false }, firestorePluginOptions);
}
// a Magnetar plugin is a single function that returns a `PluginInstance`
// the plugin implements the logic for all actions that a can be called from a Magnetar module instance
// each action must have the proper for both collection and doc type modules
/**
 * It's required to pass the Firestore instance to make sure there are not two separate instances running which can cause issues.
 * As long as Firebase is initialized before you pass it, you can just import and pass it like so:
 * @example
 * ```js
 * import { initializeApp } from 'firebase/app'
 * import { getFirestore } from 'firebase/firestore'
 * import { CreatePlugin as FirestorePlugin } from '@magnetarjs/firestore'
 *
 * const firebaseApp = initializeApp({  }) // pass config
 * const db = getFirestore(firebaseApp)
 *
 * // initialise plugin
 * const remote = FirestorePlugin({ db })
 * ```
 */
const CreatePlugin = (firestorePluginOptions) => {
    const pluginOptions = firestorePluginOptionsWithDefaults(firestorePluginOptions);
    const batchSync = batchSyncFactory(pluginOptions);
    // the plugin must try to implement logic for every `ActionName`
    const fetch = fetchActionFactory(pluginOptions);
    const stream = streamActionFactory(pluginOptions);
    const insert = insertActionFactory(batchSync, pluginOptions);
    const _merge = writeActionFactory(batchSync, pluginOptions, 'merge');
    const assign = writeActionFactory(batchSync, pluginOptions, 'assign');
    const replace = writeActionFactory(batchSync, pluginOptions, 'replace');
    const deleteProp = deletePropActionFactory(batchSync, pluginOptions);
    const _delete = deleteActionFactory(batchSync, pluginOptions);
    const actions = {
        fetch,
        stream,
        insert,
        merge: _merge,
        assign,
        replace,
        deleteProp,
        delete: _delete,
    };
    const revert = revertActionFactory(actions);
    // the plugin function must return a `PluginInstance`
    const instance = {
        revert,
        actions,
        syncPendingWrites: batchSync.forceSyncEarly,
    };
    return instance;
};

export { CreatePlugin };
